import type { Address } from "viem"

import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { IChunkedRpcStrategy } from "@/domains/library/rpc-strategy"
import { OnChainService } from "@/domains/on-chain"
import { TokenStore } from "@/domains/tokens/token-store"

export class TokenOnChainTransport {
  constructor(
    private onChainService: OnChainService,
    private chunkedRpcStrategy: IChunkedRpcStrategy,
  ) {}

  /**
   * Update token balances in chunks with error handling
   * Pure data loading - no state, no caching, no side effects
   */
  async updateBalances(
    userAddress: string,
    tokens: TokenStore[],
    query: IOrchestratedQuerySender<any>,
    defaultChunkSize: number = 50,
  ): Promise<Map<string, bigint>> {
    const allBalances = new Map<string, bigint>()

    // Use strategy to execute chunked RPC calls
    const chunkResults = await this.chunkedRpcStrategy.executeChunked(
      tokens,
      query,
      defaultChunkSize,
      async (chunk: TokenStore[], chunkIndex: number) => {
        // Extract addresses for this chunk
        const tokenAddresses = chunk.map((token) => token.address.toString() as Address)

        // Fetch balances for this chunk
        const balances = await this.onChainService.getTokenBalances(userAddress as `0x${string}`, tokenAddresses)

        // Collect all balances
        balances.forEach((balance, address) => {
          allBalances.set(address, balance)
        })

        // Return balances as array of entries for metrics recording
        return Array.from(balances.entries())
      },
      query.getRpcMetricsCollector(),
    )

    return allBalances
  }

  /**
   * Get native token (ETH) balance for a user address
   * Pure data loading - no state, no caching, no side effects
   */
  async getNativeBalance(
    userAddress: string,
    query: IOrchestratedQuerySender<bigint>,
    // Note: query parameter available for metrics, but native balance doesn't need chunking
  ): Promise<bigint> {
    return await this.onChainService.getNativeBalance(userAddress as Address)
  }
}
